import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useGoogleAds, useMetaAds } from 'hooks';
import toast from 'react-hot-toast';

const AdsContext = createContext();

export const useAds = () => {
  const ctx = useContext(AdsContext);
  if (!ctx) throw new Error('useAds debe usarse dentro de un AdsProvider');
  return ctx;
};

export const AdsProvider = ({ children }) => {
  const {
    isConnected: isGoogleConnected,
    activeAccount: rawGoogleAccount,
    loadingActiveAccount: loadingGoogle,
    fetchGoogleUserInfo,
    googleUserInfo,
    loadingUserInfo: loadingGoogleUser
  } = useGoogleAds();

  const {
    isConnected: isMetaConnected,
    activeAccount: rawMetaAccount,
    loadingActiveAccount: loadingMeta,
    fetchMetaUserInfo,
    metaUserInfo,
    loadingUserInfo: loadingMetaUser
  } = useMetaAds();

  const [activeGoogleAccount, setActiveGoogleAccount] = useState(null);
  const [activeMetaAccount, setActiveMetaAccount] = useState(null);
  const [googleProfile, setGoogleProfile] = useState(null);
  const [metaProfile, setMetaProfile] = useState(null);

  const fetchGoogleInfo = useCallback(async () => {
    try {
      if (isGoogleConnected) {
        const profile = await fetchGoogleUserInfo();
        setGoogleProfile(profile);
      }
    } catch (err) {
      const errorMessage = `Error obteniendo info de usuario de Google: ${err.message}`;
      console.error('Error obteniendo info de usuario:', err);
      toast.error(errorMessage);
    }
  }, [isGoogleConnected, fetchGoogleUserInfo]);

  const fetchMetaInfo = useCallback(async () => {
    try {
      if (isMetaConnected) {
        const profile = await fetchMetaUserInfo();
        setMetaProfile(profile);
      }
    } catch (err) {
      const errorMessage = `Error obteniendo info de usuario de Meta: ${err.message}`;
      console.error('Error obteniendo info de usuario:', err);
      toast.error(errorMessage);
    }
  }, [isMetaConnected, fetchMetaUserInfo]);

  // Only fetch user info on mount, not active accounts (hooks handle that)
  useEffect(() => {
    fetchGoogleInfo();
    fetchMetaInfo();
    // Note: fetchAllActiveAccounts removed - useGoogleAds and useMetaAds hooks
    // already handle fetching active accounts when connected
  }, [
    fetchGoogleInfo,
    fetchMetaInfo
  ]);

  // Note: Removed redundant fetchActiveAccount calls here because:
  // 1. useGoogleAds hook already fetches active account when isConnected changes
  // 2. useMetaAds hook already fetches active account when isConnected changes
  // 3. These hooks are called by AdsProvider, so they handle the fetching

  // Limpiar cuentas activas cuando se desconecta alguna plataforma
  useEffect(() => {
    if (!isGoogleConnected) {
      setActiveGoogleAccount(null);
      setGoogleProfile(null);
    }
  }, [isGoogleConnected]);

  useEffect(() => {
    if (!isMetaConnected) {
      setActiveMetaAccount(null);
      setMetaProfile(null);
    }
  }, [isMetaConnected]);

  const loadingActiveAccounts = loadingGoogle || loadingMeta;
  const loadingUserProfiles = loadingGoogleUser || loadingMetaUser;

  // Sync local state when rawAccount changes
  useEffect(() => {
    if (rawGoogleAccount) {
      setActiveGoogleAccount(rawGoogleAccount);
    }
  }, [rawGoogleAccount]);

  useEffect(() => {
    if (rawMetaAccount) {
      setActiveMetaAccount(rawMetaAccount);
    }
  }, [rawMetaAccount]);

  return (
    <AdsContext.Provider
      value={{
        activeGoogleAccount: isGoogleConnected ? rawGoogleAccount || activeGoogleAccount : null,
        activeMetaAccount: isMetaConnected ? rawMetaAccount || activeMetaAccount : null,
        loadingActiveAccounts,
        googleUserInfo: googleUserInfo || googleProfile,
        metaUserInfo: metaUserInfo || metaProfile,
        loadingUserProfiles
      }}
    >
      {children}
    </AdsContext.Provider>
  );
};
